/* eslint-disable no-nested-ternary */
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PalletCalculator } from '@components/PalletCalculator';
import { PackagingBreakdown } from '@components/PackagingBreakdown';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { Button } from '../../../tailwindComponents/Button/Button';
import { ProductLinesSection } from './ProductLinesSection';

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2' />
      <path d='M15 18H9' />
      <path d='M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14' />
      <circle cx='17' cy='18' r='2' />
      <circle cx='7' cy='18' r='2' />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' />
      <circle cx='12' cy='10' r='3' />
    </svg>
  );
}

function NoteAddIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
      <polyline points='14 2 14 8 20 8' />
      <line x1='12' x2='12' y1='18' y2='12' />
      <line x1='9' x2='15' y1='15' y2='15' />
    </svg>
  );
}

interface OutboundShipmentNoteDetailsProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  primaryAction?: () => void;
  embedded?: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}

export function OutboundShipmentNoteDetails({
  selectedOutboundProcess,
  onClose,
  primaryAction,
  embedded,
  isDesktop,
  isTablet,
}: OutboundShipmentNoteDetailsProps) {
  const {
    pickingConfirmation,
    shipmentNote,
    despatchNote,
    notices,
    goodsReceivedNote,
  } = selectedOutboundProcess;

  if (!despatchNote) {
    return null;
  }

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete = !!goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const isDespatchNote = !shipmentNote;
  const noteLabel = isDespatchNote ? 'Despatch note' : 'Shipment note';
  const noteNumber =
    shipmentNote?.shipmentNoteNumber ?? despatchNote.despatchNoteNumber;
  const customerName =
    shipmentNote?.customerName ??
    despatchNote.customer?.name ??
    despatchNote.customerName ??
    'N/A';
  const warehouseAddress =
    shipmentNote?.warehouse?.address ?? despatchNote.warehouse?.address;
  const comment = shipmentNote?.comment ?? despatchNote.comment;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(noteNumber);
  };

  return (
    <div className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            {noteLabel} #{noteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label={`Copy ${noteLabel.toLowerCase()} number`}
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Customer name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Customer name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {customerName}
          </span>
        </div>

        {/* Warehouse address */}
        {warehouseAddress && (
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-1.5 text-gray-500'>
              <MapPinIcon />
              <span className='text-xs font-medium'>Warehouse address</span>
            </div>
            <span className='text-sm font-medium text-gray-900'>
              {warehouseAddress}
            </span>
          </div>
        )}

        {/* Shipped with (only available on shipment notes) */}
        {shipmentNote?.supplierName && (
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-1.5 text-gray-500'>
              <TruckIcon />
              <span className='text-xs font-medium'>Shipped with</span>
            </div>
            <span className='text-sm font-medium text-gray-900'>
              {shipmentNote.supplierName}
            </span>
          </div>
        )}

        {/* Required time of arrival */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {`${dayjs(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('D MMM YYYY')} · by ${dayjs(
              despatchNote.requiredTimeOfArrival.dateTime
            )
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('HH:mm')}`}
          </span>
        </div>
      </div>

      {/* Second row of metadata */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* ETA - use shipment note ETA when available, otherwise show despatch note required ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {shipmentNote?.estimatedTimeOfArrival
              ? dayjs(shipmentNote.estimatedTimeOfArrival.dateTime)
                  .tz(shipmentNote.estimatedTimeOfArrival.timeZone)
                  .format('D MMM YYYY · HH:mm')
              : dayjs(despatchNote.requiredTimeOfArrival.dateTime)
                  .tz(despatchNote.requiredTimeOfArrival.timeZone)
                  .format('D MMM YYYY · HH:mm')}
          </span>
        </div>

        {/* Created date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Created</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(shipmentNote?.createdAt ?? despatchNote.createdAt).format(
              'D MMM YYYY'
            )}
          </span>
        </div>
      </div>

      {/* Issues */}
      {hasIssues && (
        <div className='px-6 py-4 border-b border-gray-200'>
          <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
            Issues
          </p>
          <div className='space-y-2'>
            {notices?.map((notice) => (
              <div
                key={notice}
                className={`flex items-center gap-2 p-2 rounded ${
                  hasIssuesBeforeProcessIsComplete ? 'bg-amber-50' : 'bg-red-50'
                }`}
              >
                <ExclamationTriangleIcon
                  className={`w-4 h-4 flex-shrink-0 ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                />
                <span
                  className={`text-sm ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-800'
                      : 'text-red-800'
                  }`}
                >
                  {notice}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>Items</h3>

        {shipmentNote ? (
          /* Shipment Note SKUs - with picked/discrepancy columns */
          <>
            {isDesktop && (
              <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
                <span className='text-xs font-semibold text-gray-500 text-center'>
                  #
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  SKU ID
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  SKU name
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  Picked SKUs
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  Despatched SKUs
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  Despatched Pallets
                </span>
              </div>
            )}

            <div className='flex flex-col gap-4'>
              {shipmentNote.skus.map((sku, index) => {
                const isDiscrepancy = sku.discrepancyReason;
                const pickingConfirmationSku = pickingConfirmation?.skus.find(
                  (pickingSku) => pickingSku.skuId === sku.skuId
                );
                const pickedQuantity = pickingConfirmationSku?.pickedQuantity;

                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const discrepancyAmount = pickedQuantity! - sku.skuQuantity;
                const isNegativeDiscrepancy = discrepancyAmount > 0;

                const wholeShipped =
                  pickingConfirmationSku?.wholeUnitsPicked ?? 0;
                const productFactor =
                  pickingConfirmationSku?.amountOfProductInSku ?? 1;
                const looseProducts = Math.max(
                  0,
                  (pickingConfirmationSku?.productsPicked ?? 0) -
                    wholeShipped * productFactor
                );
                const hasBreakdown =
                  (pickingConfirmationSku?.packaging?.length ?? 0) > 0 ||
                  looseProducts > 0;

                const hasPackagingInfo = sku.packagingDetails?.packagingInfo;

                let amountOfSkusInLayer: number | undefined;
                let amountOfLayersInPallet: number | undefined;
                let packagingInfoType: string | undefined;
                let palletisingType: string | null | undefined;

                if (hasPackagingInfo) {
                  amountOfSkusInLayer =
                    sku.packagingDetails?.packagingInfo?.amountOfSkusInLayer;
                  amountOfLayersInPallet =
                    sku.packagingDetails?.packagingInfo?.amountOfLayersInPallet;
                  packagingInfoType = sku.packagingDetails?.packagingInfo?.type;
                } else {
                  palletisingType = sku.packagingDetails?.palletisingType;
                }

                return (
                  <div
                    key={sku.skuId}
                    className={`${
                      index !== shipmentNote.skus.length - 1
                        ? 'pb-4 border-b border-gray-100'
                        : ''
                    }`}
                  >
                    {isDesktop ? (
                      <div className='flex flex-col gap-3'>
                        <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1fr_1.5fr] gap-4 items-start'>
                          <div className='flex justify-center'>
                            <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                              {index + 1}
                            </span>
                          </div>
                          <span className='text-sm text-gray-900'>
                            <SkuInfoLink inventorySkuId={sku.skuId}>
                              {sku.skuIdentifier}
                            </SkuInfoLink>
                          </span>
                          <span className='text-sm text-gray-900'>
                            {sku.name}
                          </span>
                          <span className='text-sm text-gray-900'>
                            {pickedQuantity}
                          </span>
                          <span className='text-sm text-gray-900'>
                            {sku.skuQuantity}
                            {sku.discrepancyReason && (
                              <span className='text-red-600 ml-1'>
                                {`(${isNegativeDiscrepancy ? '-' : '+'}${
                                  isNegativeDiscrepancy
                                    ? discrepancyAmount
                                    : discrepancyAmount.toString().substring(1)
                                })`}
                              </span>
                            )}
                          </span>
                          <span className='text-sm text-gray-900 whitespace-nowrap'>
                            {hasPackagingInfo ? (
                              <PalletCalculator
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                palletName={packagingInfoType!}
                                skuQuantity={sku.skuQuantity}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                amountOfSkusInLayer={amountOfSkusInLayer!}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                amountOfLayersInPallet={amountOfLayersInPallet!}
                              />
                            ) : (
                              palletisingType
                            )}
                          </span>
                        </div>

                        {isDiscrepancy && (
                          <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                            <div />
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                Discrepancy reason
                              </span>
                              <span className='text-sm text-gray-600'>
                                {sku.discrepancyReason}
                              </span>
                            </div>
                          </div>
                        )}

                        {hasBreakdown && (
                          <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                            <div />
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                Breakdown
                              </span>
                              <PackagingBreakdown
                                packaging={pickingConfirmationSku?.packaging}
                                looseProducts={looseProducts}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='flex gap-3'>
                        <div className='flex-shrink-0'>
                          <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                            {index + 1}
                          </span>
                        </div>
                        <div
                          className={`flex-1 grid gap-3 ${
                            isTablet ? 'grid-cols-2' : 'grid-cols-1'
                          }`}
                        >
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              SKU ID
                            </span>
                            <span className='text-sm text-gray-900'>
                              <SkuInfoLink inventorySkuId={sku.skuId}>
                                {sku.skuIdentifier}
                              </SkuInfoLink>
                            </span>
                          </div>
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              SKU name
                            </span>
                            <span className='text-sm text-gray-900'>
                              {sku.name}
                            </span>
                          </div>
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              Picked SKUs
                            </span>
                            <span className='text-sm text-gray-900'>
                              {pickedQuantity}
                            </span>
                          </div>
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              Despatched SKUs
                            </span>
                            <span className='text-sm text-gray-900'>
                              {sku.skuQuantity}
                              {sku.discrepancyReason && (
                                <span className='text-red-600 ml-1'>
                                  {`(${isNegativeDiscrepancy ? '-' : '+'}${
                                    isNegativeDiscrepancy
                                      ? discrepancyAmount
                                      : discrepancyAmount
                                          .toString()
                                          .substring(1)
                                  })`}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              Despatched Pallets
                            </span>
                            <span className='text-sm text-gray-900'>
                              {hasPackagingInfo ? (
                                <PalletCalculator
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  palletName={packagingInfoType!}
                                  skuQuantity={sku.skuQuantity}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfSkusInLayer={amountOfSkusInLayer!}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfLayersInPallet={
                                    amountOfLayersInPallet!
                                  }
                                />
                              ) : (
                                palletisingType
                              )}
                            </span>
                          </div>
                          {isDiscrepancy && (
                            <div
                              className={`flex flex-col gap-0.5 ${
                                isTablet ? 'col-span-2' : ''
                              }`}
                            >
                              <span className='text-xs font-medium text-gray-500'>
                                Discrepancy reason
                              </span>
                              <span className='text-sm text-gray-600'>
                                {sku.discrepancyReason}
                              </span>
                            </div>
                          )}
                          {hasBreakdown && (
                            <div
                              className={`flex flex-col gap-0.5 ${
                                isTablet ? 'col-span-2' : ''
                              }`}
                            >
                              <span className='text-xs font-medium text-gray-500'>
                                Breakdown
                              </span>
                              <PackagingBreakdown
                                packaging={pickingConfirmationSku?.packaging}
                                looseProducts={looseProducts}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          /* Despatch Note SKUs - simpler layout without picked/discrepancy */
          <>
            {despatchNote.skus.length > 0 && isDesktop && (
              <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
                <span className='text-xs font-semibold text-gray-500 text-center'>
                  #
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  SKU ID
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  SKU name
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  Quantity
                </span>
                <span className='text-xs font-semibold text-gray-500'>
                  Pallets
                </span>
              </div>
            )}

            {despatchNote.skus.length > 0 && (
              <div className='flex flex-col gap-4'>
                {despatchNote.skus.map((sku, index) => {
                  const hasPackagingInfo = sku.packagingDetails?.packagingInfo;

                  let amountOfSkusInLayer: number | undefined;
                  let amountOfLayersInPallet: number | undefined;
                  let packagingInfoType: string | undefined;
                  let palletisingType: string | null | undefined;

                  if (hasPackagingInfo) {
                    amountOfSkusInLayer =
                      sku.packagingDetails?.packagingInfo?.amountOfSkusInLayer;
                    amountOfLayersInPallet =
                      sku.packagingDetails?.packagingInfo
                        ?.amountOfLayersInPallet;
                    packagingInfoType =
                      sku.packagingDetails?.packagingInfo?.type;
                  } else {
                    palletisingType = sku.packagingDetails?.palletisingType;
                  }

                  return (
                    <div
                      key={sku.skuId}
                      className={`${
                        index !== despatchNote.skus.length - 1
                          ? 'pb-4 border-b border-gray-100'
                          : ''
                      }`}
                    >
                      {isDesktop ? (
                        <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1.5fr] gap-4 items-start'>
                          <div className='flex justify-center'>
                            <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                              {index + 1}
                            </span>
                          </div>
                          <span className='text-sm text-gray-900'>
                            <SkuInfoLink inventorySkuId={sku.skuId}>
                              {sku.skuIdentifier}
                            </SkuInfoLink>
                          </span>
                          <span className='text-sm text-gray-900'>
                            {sku.name}
                          </span>
                          <span className='text-sm text-gray-900'>
                            {sku.skuQuantity}
                          </span>
                          <span className='text-sm text-gray-900 whitespace-nowrap'>
                            {hasPackagingInfo ? (
                              <PalletCalculator
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                palletName={packagingInfoType!}
                                skuQuantity={sku.skuQuantity}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                amountOfSkusInLayer={amountOfSkusInLayer!}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                amountOfLayersInPallet={amountOfLayersInPallet!}
                              />
                            ) : (
                              palletisingType
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className='flex gap-3'>
                          <div className='flex-shrink-0'>
                            <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                              {index + 1}
                            </span>
                          </div>
                          <div
                            className={`flex-1 grid gap-3 ${
                              isTablet ? 'grid-cols-2' : 'grid-cols-1'
                            }`}
                          >
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                SKU ID
                              </span>
                              <span className='text-sm text-gray-900'>
                                <SkuInfoLink inventorySkuId={sku.skuId}>
                                  {sku.skuIdentifier}
                                </SkuInfoLink>
                              </span>
                            </div>
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                SKU name
                              </span>
                              <span className='text-sm text-gray-900'>
                                {sku.name}
                              </span>
                            </div>
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                Quantity
                              </span>
                              <span className='text-sm text-gray-900'>
                                {sku.skuQuantity}
                              </span>
                            </div>
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-xs font-medium text-gray-500'>
                                Pallets
                              </span>
                              <span className='text-sm text-gray-900'>
                                {hasPackagingInfo ? (
                                  <PalletCalculator
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    palletName={packagingInfoType!}
                                    skuQuantity={sku.skuQuantity}
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    amountOfSkusInLayer={amountOfSkusInLayer!}
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    amountOfLayersInPallet={
                                      amountOfLayersInPallet!
                                    }
                                  />
                                ) : (
                                  palletisingType
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(despatchNote.products?.length ?? 0) > 0 && (
              <div className={despatchNote.skus.length > 0 ? 'mt-6' : ''}>
                <h4 className='text-sm font-semibold text-gray-900 mb-4'>
                  Products
                </h4>

                <ProductLinesSection
                  products={despatchNote.products ?? []}
                  plannedLines={
                    selectedOutboundProcess.pickingNote?.plannedLines
                  }
                  isDesktop={isDesktop}
                  isTablet={isTablet}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Comments Section */}
      {comment && (
        <div className='px-6 py-4 border-t border-gray-200'>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Comment</h3>
          <p className='text-sm text-gray-600'>{comment}</p>
        </div>
      )}

      {!embedded && (
        <div
          className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
            isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
          }`}
        >
          <Button
            label='Close'
            intent='secondary'
            size='sm'
            onClick={onClose}
            className={!isDesktop ? 'w-full' : ''}
          />
          {primaryAction && (
            <Button
              label='Create Proof of Delivery'
              intent='primary'
              size='sm'
              startIcon={<NoteAddIcon />}
              onClick={primaryAction}
              className={!isDesktop ? 'w-full' : ''}
            />
          )}
        </div>
      )}
    </div>
  );
}
